import React from 'react';
import CostWidget from './CostWidget';
import ComplianceWidget from './ComplianceWidget';
import CarbonWidget from './CarbonWidget';
import EnergyWidget from './EnergyWidget';
import ShadowWidget from './ShadowWidget';
import ComfortWidget from './ComfortWidget';
import TextReportWidget from './TextReportWidget';
import SolarRadiationWidget from './SolarRadiationWidget';
import { tryParseJson } from './utils';

/**
 * ArchieResponseHandler Component
 * 
 * Routes responseData to the appropriate widget based on: 
 * 1. Tags in the response data (preferred method)
 * 2. Data structure detection (fallback)
 * 
 * REFACTORED:  Removed KPI widget, added direct routing to Carbon, Energy, Shadow widgets
 * 
 * @param {Object} props
 * @param {Object} props.responseData - Response data from getArchieSupport API
 * @param {boolean} props.responseData.csv_representation - Flag indicating CSV data
 * @param {string} props.responseData.response - The actual data (CSV format or text)
 * @param {Array} props.responseData.tags - Optional tags indicating data domain
 * @param {boolean} props.isRegulation - Legacy flag indicating compliance/regulation data
 * @param {Function} props.onComplete - Callback when rendering is complete
 */
const ArchieResponseHandler = ({ responseData, isRegulation, onComplete }) => {
    /**
     * Determine widget type based on tags and data structure
     */
    const getWidgetType = (data, isReg) => {
        // Check for tags first (preferred method)
        if (data.tags && data.tags.data_characteristics) {
            const characteristics = data.tags.data_characteristics;
            
            if (characteristics.includes('cost') || characteristics.includes('boq')) {
                return 'cost';
            }
            if (characteristics.includes('compliance') || characteristics.includes('zoning') || characteristics.includes('regulation')) {
                return 'compliance';
            }
            if (characteristics.includes('text') || characteristics.includes('report')) {
                return 'text';
            }
            if (characteristics.includes('Carbon Footprint') || characteristics.includes('carbon')) {
                return 'carbon';
            }
            if (characteristics.includes('Energy Demand') || characteristics.includes('energy')) {
                return 'energy';
            }
            if (characteristics.includes('Shadow Analysis') || characteristics.includes('shadow')) {
                return 'shadow';
            }
            if (characteristics.includes('Solar Radiation')) {
                return 'solar';
            }
            if (characteristics.includes('Comfort')) {
                return 'comfort';
            }
        }

        // Check legacy isRegulation flag
        if (isReg) {
            return 'compliance';
        }

        // Check for JSON data structure (system -> subsystem -> metrics)
        if (data.response) {
            try {
                const parsedJson = tryParseJson(data. response);
                
                // If it's a hierarchical JSON structure
                if (parsedJson && typeof parsedJson === 'object' && !Array.isArray(parsedJson)) {
                    const firstKey = Object.keys(parsedJson)[0];
                    if (firstKey && typeof parsedJson[firstKey] === 'object') {
                        // Check if this looks like system data
                        const systemData = parsedJson[firstKey];
                        const subsystemKeys = Object.keys(systemData);
                        
                        if (subsystemKeys.length > 0) {
                            const firstSubsystem = systemData[subsystemKeys[0]];
                            
                            if (typeof firstSubsystem === 'object' && !Array.isArray(firstSubsystem)) {
                                // Detect type based on attribute names
                                const attrKeys = Object.keys(firstSubsystem).map(k => k.toLowerCase());
                                
                                if (attrKeys. some(k => k.includes('carbon') || k.includes('co2'))) {
                                    return 'carbon';
                                }
                                if (attrKeys. some(k => k.includes('energy') || k.includes('demand') || k.includes('kwh'))) {
                                    return 'energy';
                                }
                                if (attrKeys.some(k => k. includes('shadow') || k.includes('shade'))) {
                                    return 'shadow';
                                }
                            }
                        }
                    }
                }
            } catch (err) {
                console.error('Error detecting widget type from JSON:', err);
            }
        }

        // Fallback to data structure detection for CSV
        if (data.csv_representation && data.response) {
            try {
                const firstLine = data.response.split('\n')[0];
                
                if (firstLine.includes('Cost Category')) {
                    return 'cost';
                }
                if (firstLine.includes('Zoning Regulations') || firstLine.includes('REGULATION')) {
                    return 'compliance';
                }
            } catch (err) {
                console.error('Error detecting widget type from CSV:', err);
            }
        }

        // Check for HTML content
        if (data.response && data.response.indexOf('DOCTYPE html') > -1) {
            return 'html';
        }

        // Default to text for plain responses
        return 'text';
    };

    const widgetType = getWidgetType(responseData, isRegulation);

    console.log('[ArchieResponseHandler] Routing to widget type:', widgetType);

    // Route to appropriate widget
    switch (widgetType) {
        case 'cost':
            return <CostWidget data={responseData} onComplete={onComplete} />;
        
        case 'compliance':
            return <ComplianceWidget data={responseData} onComplete={onComplete} />;
        
        case 'carbon':
            return <CarbonWidget data={responseData} onComplete={onComplete} />;
        
        case 'energy':
            return <EnergyWidget data={responseData} onComplete={onComplete} />;
        
        case 'shadow': 
            return <ShadowWidget data={responseData} onComplete={onComplete} />;

        case 'comfort':
            return <ComfortWidget data={responseData} onComplete={onComplete} />;

        case 'solar':
            return <SolarRadiationWidget data={responseData} onComplete={onComplete} />;

        case 'html':
            // For HTML content, render directly (legacy support)
            return (
                <div className="csv-data-outer">
                    <div className="csv-table-div" dangerouslySetInnerHTML={{ __html: responseData.response }} />
                </div>
            );
        
        case 'text':
        default:
            return <TextReportWidget data={responseData} onComplete={onComplete} />;
    }
};

export default ArchieResponseHandler;